import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Download } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AspectPreset, Photo } from '../../../types/photo';
import { TRIAXIAL_PHOTO_CATEGORIES, type TriaxialPhotoCategoryId } from '../../../types/triaxialPhoto';
import * as photoService from '../../../services/photoService';
import { usePermissions } from '../../../hooks/usePermissions';
import { TriaxialPhotoCategorySection } from './TriaxialPhotoCategory';
import { CropDialog } from './CropDialog';
import type { SlotUploadState } from './PhotoSlotComponents';

interface Props {
  workOrderDetailKey: string;
  /**
   * Categories to offer (default: all three). Rock TCS / UCS pass
   * ['before', 'after'] — their photo section caps at Before + After with no
   * close-up slot (BUG-2026-0137 §12). Existing close-up photos on those
   * tests stay in the DB; they are simply no longer shown or added here.
   */
  categories?: TriaxialPhotoCategoryId[];
  /** Default crop aspect (W:H) handed to the recrop dialog (BUG-2026-0137 §11). */
  cropAspect?: number;
  /** Crop preset list override — Rock TCS/UCS/UCS-NS pass
   *  ROCK_REPORT_ASPECT_PRESETS (single "Report (3:5)" ratio). */
  cropPresets?: readonly AspectPreset[];
  /** Cap per category — Rock TCS/UCS/UCS-NS pass 1 so the "Add another photo"
   *  tile disappears once Before + After each have their photo
   *  (BUG-2026-0137/0138 rework). Default: unlimited (triaxial). */
  maxPerCategory?: number;
}

interface CropState {
  imageUrl: string;
  fileName: string;
  photoKey: string;
  fullSelection?: boolean;
}

/**
 * FEAT-2026-0006: Triaxial-specific Photos tab.
 *
 * Replaces the rock-direct-shear 12-slot grid with three free-form categories
 * (Sample Before Testing, Sample After Testing, Close-up Photo of Sample).
 * Each category holds a user-chosen number of photos, indexed by Position
 * "1", "2", ... so existing recrop / comment / delete plumbing works without
 * a DB migration.
 */
export const TriaxialPhotosTab = ({ workOrderDetailKey, categories, cropAspect, cropPresets, maxPerCategory }: Props) => {
  const { canEditTestData } = usePermissions();
  const visibleCategories = useMemo(
    () => (categories ? TRIAXIAL_PHOTO_CATEGORIES.filter((c) => categories.includes(c.id)) : TRIAXIAL_PHOTO_CATEGORIES),
    [categories],
  );
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadByCategory, setUploadByCategory] = useState<Partial<Record<TriaxialPhotoCategoryId, SlotUploadState>>>({});
  const [cropState, setCropState] = useState<CropState | null>(null);

  const fetchPhotos = useCallback(async () => {
    try {
      setError(null);
      const data = await photoService.getPhotos(workOrderDetailKey);
      setPhotos(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [workOrderDetailKey]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  /** Photos grouped & sorted by category. */
  const photosByCategory = useMemo(() => {
    const byCat: Record<TriaxialPhotoCategoryId, Photo[]> = { before: [], after: [], closeup: [] };
    for (const p of photos) {
      const cat = p.Stage as TriaxialPhotoCategoryId;
      if (cat in byCat && p.UploadStatus === 'complete') byCat[cat].push(p);
    }
    // Sort each category by numeric Position
    (Object.keys(byCat) as TriaxialPhotoCategoryId[]).forEach((k) => {
      byCat[k].sort((a, b) => Number(a.Position || 0) - Number(b.Position || 0));
    });
    return byCat;
  }, [photos]);

  const totalCount = visibleCategories.reduce((sum, cat) => sum + photosByCategory[cat.id].length, 0);

  const handleAddPhoto = useCallback(async (category: TriaxialPhotoCategoryId, file: File) => {
    const existing = photosByCategory[category];
    // Compute next free position: max(existing) + 1, or 1 if empty.
    const usedPositions = new Set(existing.map((p) => Number(p.Position || 0)));
    let nextPos = 1;
    while (usedPositions.has(nextPos)) nextPos += 1;
    const position = String(nextPos);

    setUploadByCategory((s) => ({ ...s, [category]: { progress: 0, status: 'uploading', step: 'uploading' } }));
    try {
      const SUB: Record<string, number> = { uploading: 10, processing: 50, saving: 85, done: 100 };
      await photoService.slotUpload(workOrderDetailKey, file, category, position, 'average', (evt) => {
        setUploadByCategory((s) => ({
          ...s,
          [category]: { progress: SUB[evt.type] ?? 0, status: 'uploading', step: evt.type },
        }));
      });
      setUploadByCategory((s) => ({ ...s, [category]: undefined }));
      await fetchPhotos();
    } catch (err: any) {
      setError(err?.message || `Failed to add photo to ${category}`);
      setUploadByCategory((s) => ({ ...s, [category]: { progress: 0, status: 'error' } }));
    }
  }, [workOrderDetailKey, photosByCategory, fetchPhotos]);

  const handleRecrop = useCallback((photo: Photo) => {
    setCropState({
      imageUrl: photo.originalImageUrl || photo.imageUrl,
      fileName: photo.FileName,
      photoKey: photo.PhotoKey,
    });
  }, []);

  const handleEditCropped = useCallback((photo: Photo) => {
    setCropState({
      imageUrl: photo.imageUrl,
      fileName: photo.FileName,
      photoKey: photo.PhotoKey,
      fullSelection: true,
    });
  }, []);

  const handleCropConfirm = useCallback(async (croppedFile: File) => {
    if (!cropState) return;
    const { photoKey, imageUrl } = cropState;
    setCropState(null);
    if (imageUrl.startsWith('blob:')) URL.revokeObjectURL(imageUrl);
    try {
      const { uploadUrl } = await photoService.requestRecropUrl(photoKey);
      await photoService.uploadToBlob(uploadUrl, croppedFile);
      await photoService.confirmRecrop(photoKey);
      await fetchPhotos();
    } catch (err: any) {
      setError(err?.message || 'Recrop failed');
    }
  }, [cropState, fetchPhotos]);

  const handleCropCancel = useCallback(() => {
    if (cropState?.imageUrl.startsWith('blob:')) URL.revokeObjectURL(cropState.imageUrl);
    setCropState(null);
  }, [cropState]);

  const handleDelete = useCallback(async (photoKey: string) => {
    try {
      await photoService.deletePhoto(photoKey);
      await fetchPhotos();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete photo');
    }
  }, [fetchPhotos]);

  const handleUpdateComment = useCallback(async (photoKey: string, comment: string | null) => {
    try {
      await photoService.updatePhoto(photoKey, { Comment: comment });
      await fetchPhotos();
    } catch (err: any) {
      setError(err?.message || 'Failed to update comment');
    }
  }, [fetchPhotos]);

  const handleDownloadAll = useCallback(async () => {
    try { await photoService.downloadZip(workOrderDetailKey); }
    catch (err: any) { setError(err?.message || 'Failed to download photos'); }
  }, [workOrderDetailKey]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Header strip */}
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {visibleCategories.length === 2
              ? 'Sample photos — Before and After testing.'
              : 'Triaxial photo workflow — three categories, user-chosen number of photos per category.'}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Download fontSize="small" />}
          disabled={totalCount === 0}
          onClick={handleDownloadAll}
          sx={{ textTransform: 'none' }}
        >
          Download All ({totalCount})
        </Button>
      </Stack>

      {visibleCategories.map((cat) => (
        <TriaxialPhotoCategorySection
          key={cat.id}
          category={cat}
          photos={photosByCategory[cat.id]}
          uploading={uploadByCategory[cat.id] ?? null}
          onAddPhoto={(file) => handleAddPhoto(cat.id, file)}
          onRecrop={handleRecrop}
          onEditCropped={handleEditCropped}
          onDelete={handleDelete}
          onUpdateComment={handleUpdateComment}
          readOnly={!canEditTestData}
          maxPhotos={maxPerCategory}
        />
      ))}

      {cropState && (
        <CropDialog
          open
          imageUrl={cropState.imageUrl}
          fileName={cropState.fileName}
          initialFullSelection={cropState.fullSelection}
          defaultAspect={cropAspect}
          presets={cropPresets}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </Box>
  );
};
